import cx from 'classnames';
import {useContext, useEffect, useMemo} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {
  navigateToRoute as navigateToRouteCreateFabric,
  useCurrentRoute as useCurrentRouteCreateFabric,
} from '~/CreateFabricInvitation/navigation';
import {eventInEditMode} from '~/CreateFabricInvitation/reducers/details/selectors';
import {selectSavingStep} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {selectIsPremium} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';
import {FabricInvitationContext} from '~/common/FabricInvitation/context/FabricInvitationContext';
import {selectEventStatus, selectIsEventPro} from '~/common/fabric/reducers/eviteEvent/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {isDraft} from '~/common/utils/event_utils';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {LoadingOverlay} from '~library/molecules/LoadingOverlay';
import {ArrowIcon} from '~library/svg/ArrowIcon';
import {PremiumIcon} from '~library/svg/PremiumIcon';
import {premiumColor} from '~libSass/base/_exports.sass';

export const NavBottom = ({
  showBackBtn = true,
  navigateToRouteHook = navigateToRouteCreateFabric,
  useCurrentRouteHook = useCurrentRouteCreateFabric,
  handleAdsToggleInNavBottom = undefined,
  handleBackButtonClick = undefined,
  handleNextButtonClick,
  disabled = false,
  children = null,
}) => {
  const {isPremiumOnMount} = useContext(FabricInvitationContext);
  let currentRoute = useCurrentRouteHook();
  if (currentRoute?.currentRoute) currentRoute = currentRoute.currentRoute; // Support for QEC
  const store = useStore();
  const dispatch = useDispatch();
  const savingStep = useSelector(selectSavingStep);
  const status = useSelector(selectEventStatus);
  const inEditMode = useSelector(eventInEditMode);
  const isPremium = useSelector(selectIsPremium);
  const isEventPro = useSelector(selectIsEventPro);
  const becamePremiumInEdit = useMemo(() => {
    if (isDraft(status)) return false;
    return !isPremiumOnMount && isPremium;
  }, [status, isPremiumOnMount, isPremium]);
  const showAdUpsellToggle =
    !isPremium && !isEventPro && !inEditMode && currentRoute.path === 'details';

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {savingStep: false},
    });
  }, []);

  return currentRoute.hideBottomNav
    ? null
    : <div
        className={cx(`create-flow__form-nav ${currentRoute.path}`, {
          'create-flow__form-nav--sticky-upsell-wrap': showAdUpsellToggle,
        })}
      >
        {showAdUpsellToggle && (
          <div className="create-flow__form-nav__sticky-ad-upsell">
            <div className="create-flow__form-nav__sticky-ad-upsell__left">
              <PremiumIcon color={premiumColor} ratio={0.08} />
              <Typography variant="paragraph2" color="charcoal">
                Want to go ad-free?
              </Typography>
            </div>
            <Button
              variant="link"
              data-qa-id="remove-ads-toggle"
              onClick={handleAdsToggleInNavBottom}
            >
              Upgrade now
            </Button>
          </div>
        )}

        {currentRoute.meta.showBackButton && showBackBtn && (
          <Button
            className="create-flow__form-nav-back"
            data-qa-id="nav-bottom-previous"
            variant="secondary"
            size="large"
            type="button"
            ariaLabel="Back"
            disabled={disabled}
            onClick={async () => {
              const check = await handleBackButtonClick?.();
              if (typeof check === 'boolean' && !check) return;
              dispatch({
                type: UPDATE_DESIGN_OPTIONS,
                payload: {savingStep: true},
              });

              let navigating = true;
              try {
                navigating = await navigateToRouteHook('previous', store);
              } catch (e) {
                evite.error('Failed to navigate', e);
              } finally {
                if (!navigating) {
                  dispatch({
                    type: UPDATE_DESIGN_OPTIONS,
                    payload: {savingStep: false},
                  });
                }
              }
            }}
          >
            <ArrowIcon direction="left" className="create-flow__form-nav-back-arrow" ratio={0.75} />
          </Button>
        )}
        {children ?? (
          <Button
            className="create-flow__form-nav-next"
            data-qa-id="nav-bottom-next"
            variant="primary"
            size="large"
            disabled={disabled}
            type="button"
            ariaLabel="Next"
            onClick={async () => {
              const check = await handleNextButtonClick?.();
              if (typeof check === 'boolean' && !check) return;
              if (becamePremiumInEdit) {
                await navigateToRouteHook('review', store);
              } else {
                dispatch({
                  type: UPDATE_DESIGN_OPTIONS,
                  payload: {savingStep: true},
                });
                let navigating;

                try {
                  navigating = await navigateToRouteHook(currentRoute.nextRoute, store);
                } catch {
                  navigating = false;
                }

                if (!navigating) {
                  dispatch({
                    type: UPDATE_DESIGN_OPTIONS,
                    payload: {savingStep: false},
                  });
                }
              }

              return null;
            }}
          >
            {becamePremiumInEdit ? 'Next: Checkout' : currentRoute.nextButtonText}
          </Button>
        )}
        <LoadingOverlay loadingMessage="Saving..." show={savingStep} />
      </div>;
};
